import { Component, OnInit, ViewChild } from '@angular/core';
import { Table } from 'primeng/table';
import { MessageService } from 'primeng/api';
import { BusquedaRolesService } from './services/busqueda-roles.service';
import { IRol, IRolResponse, IPermiso, IPermisoMenuSubmenu } from './interfaces/rol.interface';
import { SessionService } from '@shared/helpers/sesion.service';

type ViewMode = 'selection' | 'botones-campos' | 'menus-submenus';

@Component({
  selector: 'app-busqueda-roles',
  templateUrl: './busqueda-roles.component.html',
  providers: [MessageService]
})
export class BusquedaRolesComponent implements OnInit {
  @ViewChild('dt') table!: Table;
  @ViewChild('dtPermissions') permissionsTable!: Table;
  @ViewChild('dtPermissionsMenus') permissionsMenusTable!: Table;
  @ViewChild('dtMenus') menusTable!: Table;
  @ViewChild('dtSubmenus') submenusTable!: Table;

  // Control de vistas
  currentView: ViewMode = 'selection';

  dataSource: IRol[] = [];
  loading: boolean = false;
  first: number = 0;
  rows: number = 10;
  totalRecords: number = 0;

  // Dialog de permisos
  displayPermissionsDialog: boolean = false;
  selectedRol: IRol | null = null;
  permissionsData: any[] = []; // Para botones y campos
  menusSubmenusData: any[] = []; // Para vista de módulos agrupados
  menusData: any[] = []; // Para vista de menús (padres)
  submenusData: any[] = []; // Para vista de submenús (hijos)
  displayMenusDialog: boolean = false; // Dialog de menús (padres)
  displaySubmenusDialog: boolean = false; // Dialog de submenús (hijos)
  selectedModule: any = null;
  selectedMenu: any = null;
  loadingPermissions: boolean = false;
  permissionsSearchText: string = '';
  menusSearchText: string = '';
  submenusSearchText: string = '';

  // Opciones del dropdown de estados (view + enable)
  permissionStates = [
    { 
      value: 'mostrar', 
      label: 'Mostrar (Habilitado)', 
      icon: 'pi pi-eye',
      color: '#22c55e',
      view: true, 
      enable: true
    },
    { 
      value: 'mostrar-deshabilitado', 
      label: 'Mostrar (Deshabilitado)', 
      icon: 'pi pi-eye-slash',
      color: '#f59e0b',
      view: true, 
      enable: false
    },
    { 
      value: 'ocultar', 
      label: 'Ocultar', 
      icon: 'pi pi-times-circle',
      color: '#ef4444',
      view: false, 
      enable: true
    }
  ];

  // Opciones del dropdown de requerido
  requiredStates = [
    {
      value: 'no-requerido',
      label: 'No Requerido',
      icon: 'pi pi-minus-circle',
      color: '#6c757d',
      required: false
    },
    {
      value: 'requerido',
      label: 'Requerido',
      icon: 'pi pi-exclamation-circle',
      color: '#dc3545',
      required: true
    }
  ];

  // Opciones del dropdown de estados para menús (solo enable)
  menuStates = [
    {
      value: 'habilitado',
      label: 'Habilitado',
      icon: 'pi pi-check-circle',
      color: '#22c55e',
      enable: true
    },
    {
      value: 'deshabilitado',
      label: 'Deshabilitado',
      icon: 'pi pi-times-circle',
      color: '#ef4444',
      enable: false
    }
  ];

  displayedColumns = [
    { field: 'id', header: 'ID', sortable: true },
    { field: 'description', header: 'Descripción', sortable: true },
    { field: 'definition', header: 'Definición', sortable: true },
    { field: 'active', header: 'Activo', sortable: true },
    { field: 'userCreated', header: 'Usuario Creador', sortable: true },
    { field: 'dateCreated', header: 'Fecha Creación', sortable: true },
    { field: 'actions', header: 'Acciones', sortable: false }
  ];

  constructor(
    private messageService: MessageService,
    private busquedaRolesService: BusquedaRolesService,
    private sessionService: SessionService
  ) {}

  ngOnInit(): void {
    // No cargar datos automáticamente, esperar selección
  }

  // Métodos para cambiar de vista
  selectBotonesCampos(): void {
    this.currentView = 'botones-campos';
    this.loadData();
  }

  selectMenusSubmenus(): void {
    this.currentView = 'menus-submenus';
    this.loadData();
  }

  backToSelection(): void {
    this.currentView = 'selection';
    this.dataSource = [];
  }

  loadData(): void {
    this.loading = true;
    
    this.busquedaRolesService.getRoles().subscribe({
      next: (response: IRolResponse) => {
        // Verificar si la respuesta es exitosa (puede venir como 'success' o 'exito')
        const isSuccess = response.success === true || response.exito === 0;
        
        if (isSuccess && response.data && response.data.length > 0) {
          this.dataSource = response.data;
          this.totalRecords = response.data.length;
        } else if (isSuccess && response.data && response.data.length === 0) {
          this.dataSource = [];
          this.totalRecords = 0;
          this.messageService.add({
            severity: 'info',
            summary: 'Información',
            detail: 'No se encontraron registros'
          });
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudieron cargar los roles'
          });
        }
      },
      error: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al cargar los roles'
        });
        console.error('Error:', error);
      },
      complete: () => {
        this.loading = false;
      }
    });
  }

  openPermissions(row: IRol): void {
    this.selectedRol = row;
    this.displayPermissionsDialog = true;
    
    // Recargar datos frescos desde el backend
    if (this.currentView === 'menus-submenus') {
      this.loadPermissionsMenusSubmenus(row.id);
    } else {
      this.loadPermissions(row.id);
    }
  }

  loadPermissions(rolId: number): void {
    this.loadingPermissions = true;
    
    const request = {
      rolId: rolId,
      formId: '' // Puedes ajustar este ID según corresponda
    };

    this.busquedaRolesService.getPermisosRol(request).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          // Agregar los estados actuales calculados a cada permiso
          this.permissionsData = response.data.map((permission, index) => ({
            ...permission,
            currentState: this.calculateCurrentState(permission),
            currentRequired: permission.required === 1 ? 'requerido' : 'no-requerido',
            uniqueId: index // Agregar índice único temporal
          }));
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudieron cargar los permisos'
          });
          this.permissionsData = [];
        }
      },
      error: (error) => {
        console.error('Error al cargar permisos:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al cargar los permisos del rol'
        });
        this.permissionsData = [];
      },
      complete: () => {
        this.loadingPermissions = false;
      }
    });
  }

  loadPermissionsMenusSubmenus(rolId: number): void {
    this.loadingPermissions = true;
    
    const request = {
      que: 3
    };

    this.busquedaRolesService.getPermisosMenusSubmenus(request).subscribe({
      next: (response) => {
        // Validar si hay datos, independientemente del valor de success
        if (response.data && Array.isArray(response.data) && response.data.length > 0) {
          // Filtrar solo los permisos del rol seleccionado
          const permissionsFiltrados = response.data.filter(p => p.rolId === rolId);
          
          // Agrupar por módulo (moduleId)
          const modulesMap = new Map();
          
          permissionsFiltrados.forEach(permission => {
            if (!modulesMap.has(permission.moduleId)) {
              modulesMap.set(permission.moduleId, {
                moduleId: permission.moduleId,
                moduleName: permission.moduleName,
                items: [],
                enabledCount: 0,
                totalCount: 0
              });
            }
            
            const module = modulesMap.get(permission.moduleId);
            module.items.push(permission);
            module.totalCount++;
            if (permission.enable === 1) {
              module.enabledCount++;
            }
          });
          
          // Convertir el Map a array y calcular el estado general
          this.menusSubmenusData = Array.from(modulesMap.values()).map(module => ({
            ...module,
            currentState: module.enabledCount > 0 ? 'habilitado' : 'deshabilitado',
            enablePercentage: Math.round((module.enabledCount / module.totalCount) * 100)
          }));
          
          if (this.menusSubmenusData.length === 0) {
            this.messageService.add({
              severity: 'info',
              summary: 'Información',
              detail: 'No hay permisos de menús asignados a este rol'
            });
          }
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudieron cargar los permisos de menús'
          });
          this.menusSubmenusData = [];
        }
      },
      error: (error) => {
        console.error('Error al cargar permisos de menús:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al cargar los permisos de menús del rol'
        });
        this.menusSubmenusData = [];
      },
      complete: () => {
        this.loadingPermissions = false;
      }
    });
  }

  openMenusDetail(module: any): void {
    this.selectedModule = module;
    this.displayMenusDialog = true;
    
    // Recargar datos frescos del módulo desde el selectedRol
    if (this.selectedRol) {
      this.loadingPermissions = true;
      
      const request = { que: 3 };
      
      this.busquedaRolesService.getPermisosMenusSubmenus(request).subscribe({
        next: (response) => {
          if (response.data && Array.isArray(response.data) && response.data.length > 0) {
            // Filtrar por rolId
            const permissionsFiltrados = response.data.filter(p => p.rolId === this.selectedRol!.id);
            
            // Encontrar los items del módulo actual
            const moduleItems = permissionsFiltrados.filter(p => p.moduleId === module.moduleId);
            
            // Actualizar los items del módulo en memoria
            module.items = moduleItems;
            
            // Recalcular estadísticas
            module.totalCount = moduleItems.length;
            module.enabledCount = moduleItems.filter(i => i.enable === 1).length;
            module.enablePercentage = Math.round((module.enabledCount / module.totalCount) * 100);
            module.currentState = module.enabledCount > 0 ? 'habilitado' : 'deshabilitado';
            
            // Procesar la jerarquía con los datos frescos
            this.processMenuHierarchy(module);
          }
          this.loadingPermissions = false;
        },
        error: (error) => {
          console.error('Error al recargar permisos:', error);
          this.loadingPermissions = false;
          // Si hay error, usar los datos en memoria
          this.processMenuHierarchy(module);
        }
      });
    } else {
      // Si no hay rol seleccionado, usar datos en memoria
      this.processMenuHierarchy(module);
    }
  }

  processMenuHierarchy(module: any): void {
    // Obtener todos los menuIds del módulo
    const menuIds = new Set(module.items.map((item: any) => item.menuId));
    
    // 1. Identificar items que son RAÍCES (parentId: 0 o parentId no está en menuIds)
    const rootItems = module.items.filter((item: any) => 
      item.parentId === 0 || !menuIds.has(item.parentId)
    );
    
    if (rootItems.length === 0) {
      this.menusData = [];
      return;
    }
    
    // 2. Verificar si hay UNA SOLA raíz (estructura jerárquica completa)
    if (rootItems.length === 1) {
      const rootMenuId = rootItems[0].menuId;
      
      // Mostrar los hijos directos de la raíz como MENÚS
      const menusMap = new Map();
      
      module.items.forEach((item: any) => {
        // Solo agregar si su parentId es el menuId raíz (y no es la raíz misma)
        if (item.parentId === rootMenuId && item.menuId !== rootMenuId) {
          if (!menusMap.has(item.menuId)) {
            // Contar cuántos SUBMENÚS tiene este menú
            const submenus = module.items.filter((i: any) => i.parentId === item.menuId);
            
            menusMap.set(item.menuId, {
              ...item,
              currentState: item.enable === 1 ? 'habilitado' : 'deshabilitado',
              childrenCount: submenus.length,
              enabledChildrenCount: submenus.filter((c: any) => c.enable === 1).length,
              children: submenus
            });
          }
        }
      });
      
      this.menusData = Array.from(menusMap.values());
    } else {
      // 3. Hay MÚLTIPLES raíces (estructura plana, sin jerarquía intermedia)
      // Mostrar todas las raíces directamente como MENÚS
      const menusMap = new Map();
      
      rootItems.forEach((item: any) => {
        if (!menusMap.has(item.menuId)) {
          // Contar cuántos SUBMENÚS tiene este menú
          const submenus = module.items.filter((i: any) => i.parentId === item.menuId);
          
          menusMap.set(item.menuId, {
            ...item,
            currentState: item.enable === 1 ? 'habilitado' : 'deshabilitado',
            childrenCount: submenus.length,
            enabledChildrenCount: submenus.filter((c: any) => c.enable === 1).length,
            children: submenus
          });
        }
      });
      
      this.menusData = Array.from(menusMap.values());
    }
  }

  openSubmenusDetail(menu: any): void {
    this.selectedMenu = menu;
    this.displaySubmenusDialog = true;
    
    // Recargar datos frescos del módulo y actualizar el menú
    if (this.selectedRol && this.selectedModule) {
      this.loadingPermissions = true;
      
      const request = { que: 3 };
      
      this.busquedaRolesService.getPermisosMenusSubmenus(request).subscribe({
        next: (response) => {
          if (response.data && Array.isArray(response.data) && response.data.length > 0) {
            // Filtrar por rolId y moduleId
            const permissionsFiltrados = response.data.filter(p => 
              p.rolId === this.selectedRol!.id && p.moduleId === this.selectedModule.moduleId
            );
            
            // Encontrar los submenús frescos de este menú
            const freshSubmenus = permissionsFiltrados.filter((i: any) => i.parentId === menu.menuId);
            
            // Actualizar los children del menú
            menu.children = freshSubmenus;
            menu.childrenCount = freshSubmenus.length;
            menu.enabledChildrenCount = freshSubmenus.filter(c => c.enable === 1).length;
            
            // Actualizar submenusData con datos frescos
            this.submenusData = freshSubmenus.map((child: any) => ({
              ...child,
              currentState: child.enable === 1 ? 'habilitado' : 'deshabilitado'
            }));
          }
          this.loadingPermissions = false;
        },
        error: (error) => {
          console.error('Error al recargar submenús:', error);
          this.loadingPermissions = false;
          // Si hay error, usar los datos en memoria
          this.submenusData = menu.children.map((child: any) => ({
            ...child,
            currentState: child.enable === 1 ? 'habilitado' : 'deshabilitado'
          }));
        }
      });
    } else {
      // Si no hay rol seleccionado, usar datos en memoria
      this.submenusData = menu.children.map((child: any) => ({
        ...child,
        currentState: child.enable === 1 ? 'habilitado' : 'deshabilitado'
      }));
    }
  }

  closeMenusDialog(): void {
    this.displayMenusDialog = false;
    
    // Recalcular estadísticas del módulo antes de cerrar
    if (this.selectedModule && this.selectedModule.items) {
      const totalCount = this.selectedModule.items.length;
      const enabledCount = this.selectedModule.items.filter((i: any) => i.enable === 1).length;
      
      this.selectedModule.totalCount = totalCount;
      this.selectedModule.enabledCount = enabledCount;
      this.selectedModule.enablePercentage = Math.round((enabledCount / totalCount) * 100);
      this.selectedModule.currentState = enabledCount > 0 ? 'habilitado' : 'deshabilitado';
    }
    
    this.selectedModule = null;
    this.menusData = [];
    this.menusSearchText = '';
  }

  closeSubmenusDialog(): void {
    this.displaySubmenusDialog = false;
    
    // Recalcular estadísticas del menú padre antes de cerrar
    if (this.selectedMenu && this.selectedMenu.children) {
      const enabledCount = this.selectedMenu.children.filter((c: any) => c.enable === 1).length;
      this.selectedMenu.enabledChildrenCount = enabledCount;
      
      // Actualizar también en menusData
      const parentInMenusData = this.menusData.find((m: any) => m.menuId === this.selectedMenu.menuId);
      if (parentInMenusData) {
        parentInMenusData.enabledChildrenCount = enabledCount;
      }
    }
    
    this.selectedMenu = null;
    this.submenusData = [];
    this.submenusSearchText = '';
  }

  // Calcular el estado actual basado en view y enable
  calculateCurrentState(permission: any): string {
    if (permission.view === 0) {
      return 'ocultar';
    } else if (permission.view === 1 && permission.enable === 1) {
      return 'mostrar';
    } else if (permission.view === 1 && permission.enable === 0) {
      return 'mostrar-deshabilitado';
    }
    return 'ocultar'; // Por defecto
  }

  closePermissionsDialog(): void {
    this.displayPermissionsDialog = false;
    this.selectedRol = null;
    this.permissionsData = [];
    this.menusSubmenusData = [];
    this.permissionsSearchText = ''; // Limpiar texto del buscador
    
    // Cerrar también los dialogs anidados si están abiertos
    if (this.displayMenusDialog) {
      this.closeMenusDialog();
    }
    if (this.displaySubmenusDialog) {
      this.closeSubmenusDialog();
    }
    
    // Limpiar el filtro de búsqueda de las tablas
    if (this.permissionsTable) {
      this.permissionsTable.clear();
    }
    if (this.permissionsMenusTable) {
      this.permissionsMenusTable.clear();
    }
  }

  onPermissionStateChange(permission: any, event: any): void {
    const selectedState = event.value;
    
    // Buscar la configuración del estado seleccionado
    const stateConfig = this.permissionStates.find(s => s.value === selectedState);
    
    if (stateConfig) {
      // Lógica para botones y campos
      permission.view = stateConfig.view ? 1 : 0;
      permission.enable = stateConfig.enable ? 1 : 0;

      const updateRequest = {
        que: 2,
        permissionControlId: permission.id,
        controlId: 0,
        rolId: 0,
        action: 0,
        view: stateConfig.view,
        enable: stateConfig.enable,
        required: permission.required === 1,
        userCreator: this.getUserEmail(),
        formId: ''
      };

      this.savePermissionChange(updateRequest, stateConfig.label);
    }
  }

  onModuleStateChange(module: any, event: any): void {
    const selectedState = event.value;
    
    // Buscar la configuración del estado seleccionado
    const stateConfig = this.menuStates.find(s => s.value === selectedState);
    
    if (stateConfig && this.selectedRol) {
      // Construir el request para actualizar el módulo completo
      const updateRequest = {
        que: 4,
        tope: '',
        id: module.moduleId,
        menuId: 0,
        rolId: this.selectedRol.id,
        enable: stateConfig.enable ? 1 : 0,
        userCreator: this.getUserEmail()
      };

      // Ejecutar actualización del módulo
      this.busquedaRolesService.updateModuleState(updateRequest).subscribe({
        next: (response) => {
          // Actualizar el estado de todos los items en el frontend
          // (El servicio devuelve success: false pero sí realiza el cambio)
          module.items.forEach((item: any) => {
            item.enable = stateConfig.enable ? 1 : 0;
          });
          
          // Actualizar estadísticas del módulo
          module.enabledCount = stateConfig.enable ? module.totalCount : 0;
          module.enablePercentage = stateConfig.enable ? 100 : 0;
          
          this.messageService.add({
            severity: 'success',
            summary: 'Éxito',
            detail: `Módulo actualizado: ${module.totalCount} menús/submenús ${stateConfig.label.toLowerCase()}s`
          });
        },
        error: (error) => {
          console.error('Error al actualizar módulo:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al actualizar el módulo'
          });
          
          // Revertir el estado del dropdown
          module.currentState = module.enabledCount > 0 ? 'habilitado' : 'deshabilitado';
        }
      });
    }
  }

  onMenuStateChange(menu: any, event: any): void {
    const selectedState = event.value;
    
    // Buscar la configuración del estado seleccionado
    const stateConfig = this.menuStates.find(s => s.value === selectedState);
    
    if (stateConfig && this.selectedRol) {
      // Detectar si es un MENÚ (padre) o SUBMENÚ (hijo)
      // Los menús padres tienen la propiedad childrenCount
      const isMenu = menu.hasOwnProperty('childrenCount');
      const que = isMenu ? 5 : 2; // 5 para menús (padres), 2 para submenús (hijos)
      
      // Construir el request
      const updateRequest = {
        que: que,
        tope: '',
        id: menu.menuId, // El menuId del item que se está actualizando
        menuId: 0,
        rolId: this.selectedRol.id,
        enable: stateConfig.enable ? 1 : 0,
        userCreator: this.getUserEmail()
      };

      // Ejecutar actualización
      this.busquedaRolesService.updateModuleState(updateRequest).subscribe({
        next: (response) => {
          // Actualizar el estado en el frontend
          menu.enable = stateConfig.enable ? 1 : 0;
          
          // Si es un MENÚ (padre) y tiene hijos, actualizarlos también
          if (isMenu && menu.children && menu.children.length > 0) {
            menu.children.forEach((child: any) => {
              child.enable = stateConfig.enable ? 1 : 0;
            });
            
            // Actualizar estadísticas de hijos
            menu.enabledChildrenCount = stateConfig.enable ? menu.childrenCount : 0;
          } else {
            // Si es un SUBMENÚ (hijo), actualizar las estadísticas del menú padre
            // Buscar el menú padre en menusData
            if (this.selectedMenu && this.selectedMenu.children) {
              // Contar cuántos hijos están habilitados ahora
              const enabledCount = this.selectedMenu.children.filter((child: any) => child.enable === 1).length;
              this.selectedMenu.enabledChildrenCount = enabledCount;
              
              // También actualizar en menusData si existe
              const parentInMenusData = this.menusData.find((m: any) => m.menuId === this.selectedMenu.menuId);
              if (parentInMenusData) {
                parentInMenusData.enabledChildrenCount = enabledCount;
              }
            }
          }
          
          const itemType = isMenu ? 'Menú' : 'Submenú';
          this.messageService.add({
            severity: 'success',
            summary: 'Éxito',
            detail: `${itemType} actualizado: ${stateConfig.label}`
          });
        },
        error: (error) => {
          console.error('Error al actualizar:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al actualizar'
          });
          
          // Revertir el estado del dropdown
          menu.currentState = menu.enable === 1 ? 'habilitado' : 'deshabilitado';
        }
      });
    }
  }

  onRequiredStateChange(permission: any, event: any): void {
    const selectedRequired = event.value;
    
    // Buscar la configuración del estado de required
    const requiredConfig = this.requiredStates.find(s => s.value === selectedRequired);
    
    if (requiredConfig) {
      // Actualizar solo el valor de required
      permission.required = requiredConfig.required ? 1 : 0;

      // Construir el request que se enviaría
      const updateRequest = {
        que: 2,
        permissionControlId: permission.id, // Usar directamente el ID del permiso
        controlId: 0,
        rolId: 0,
        action: 0,
        view: permission.view === 1,
        enable: permission.enable === 1,
        required: requiredConfig.required,
        userCreator: this.getUserEmail(),
        formId: ''
      };

      // Ejecutar guardado
      this.savePermissionChange(updateRequest, requiredConfig.label);
    }
  }

  savePermissionChange(request: any, changeDescription: string): void {
    this.busquedaRolesService.updatePermiso(request).subscribe({
      next: (response) => {
        if (response.success) {
          this.messageService.add({
            severity: 'success',
            summary: 'Éxito',
            detail: `Permiso actualizado: ${changeDescription}`
          });
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudo actualizar el permiso'
          });
        }
      },
      error: (error) => {
        console.error('Error al actualizar permiso:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al guardar el cambio del permiso'
        });
      }
    });
  }

  saveMenuSubmenuChange(request: any, changeDescription: string): void {
    this.busquedaRolesService.updatePermisoMenuSubmenu(request).subscribe({
      next: (response) => {
        if (response.success) {
          this.messageService.add({
            severity: 'success',
            summary: 'Éxito',
            detail: `Menú/Submenú actualizado: ${changeDescription}`
          });
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudo actualizar el menú/submenú'
          });
        }
      },
      error: (error) => {
        console.error('Error al actualizar menú/submenú:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al guardar el cambio del menú/submenú'
        });
      }
    });
  }

  getUserEmail(): string {
    const userEmail = sessionStorage.getItem('Usser')?.replace(/['"]+/g, '');
    return userEmail;
  }

  onPage(event: any): void {
    this.first = event.first;
    this.rows = event.rows;
    this.loadData();
  }
}
